import { supabase } from '@/integrations/supabase/client';
import type { UserStatus } from '@/integrations/supabase/types';

export interface DeviceInfo {
  userAgent?: string;
  platform?: string;
  language?: string;
  screenResolution?: string;
  timezone?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
}

export interface SessionInfo {
  id: string;
  sessionId: string;
  deviceInfo: DeviceInfo;
  lastActivity: string;
  createdAt: string;
  expiresAt: string;
}

export interface LogoutEvent {
  userId: string;
  sessionId?: string;
  timestamp: string;
}

class SessionService {
  private currentSessionId: string | null = null;
  private logoutListeners: Set<(event: LogoutEvent) => void> = new Set();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private logoutChannel: any = null;

  constructor() {
    // Generate a unique session ID for this device
    this.currentSessionId = this.generateSessionId();

    // Note: logout listener, heartbeat, and cleanup will be started when user is initialized
  }

  private generateSessionId(): string {
    // Create a unique session ID based on timestamp and random string
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    return `${timestamp}-${random}`;
  }

  private getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenResolution = `${screen.width}x${screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Determine device type
    let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = /iPad|Android(?=.*\bMobile\b)|Opera Mobi|Windows Phone/i.test(userAgent) ? 'tablet' : 'mobile';
    }

    return {
      userAgent: userAgent.substring(0, 500), // Limit length
      platform,
      language,
      screenResolution,
      timezone,
      deviceType
    };
  }

  private setupLogoutListener() {
    // Cleanup existing channel first to prevent duplicates
    if (this.logoutChannel) {
      supabase.removeChannel(this.logoutChannel);
      this.logoutChannel = null;
    }

    // Listen for direct broadcast events (more reliable for logout events)
    const channel = supabase.channel('logout_notifications')
      .on('broadcast', { event: 'user_logout' }, (payload) => {
        const event: LogoutEvent = payload.payload;
        console.log('Received logout broadcast:', event);
        this.notifyLogoutListeners(event);
      })
      .subscribe();

    // Store the channel so we can unsubscribe later
    this.logoutChannel = channel;
  }

  private notifyLogoutListeners(event: LogoutEvent) {
    this.logoutListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in logout listener:', error);
      }
    });
  }

  // Register a logout listener
  onLogout(callback: (event: LogoutEvent) => void): () => void {
    this.logoutListeners.add(callback);
    return () => {
      this.logoutListeners.delete(callback);
    };
  }

  // Create a new session for the current user
  async createSession(userId: string): Promise<string> {
    // Generate a new session ID if we don't have one
    if (!this.currentSessionId) {
      this.currentSessionId = this.generateSessionId();
    }

    try {
      const deviceInfo = this.getDeviceInfo();
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days

      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userId,
          session_id: this.currentSessionId,
          device_info: deviceInfo as any, // Cast to Json type for Supabase
          user_agent: deviceInfo.userAgent,
          last_activity: new Date().toISOString(),
          expires_at: expiresAt.toISOString()
        })
        .select('id')
        .single();

      if (error) {
        // If session already exists, update it instead
        if (error.code === '23505') { // unique_violation
          await this.updateSessionActivity();
          return this.currentSessionId;
        }
        throw error;
      }

      return this.currentSessionId;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  // Update session activity timestamp
  async updateSessionActivity(): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({
          last_activity: new Date().toISOString(),
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        })
        .eq('session_id', this.currentSessionId);

      if (error) {
        console.error('Error updating session activity:', error);
      }
    } catch (error) {
      console.error('Error updating session activity:', error);
    }
  }

  // Get all active sessions for a user
  async getActiveSessions(userId: string): Promise<SessionInfo[]> {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('id, session_id, device_info, last_activity, created_at, expires_at')
        .eq('user_id', userId)
        .gt('expires_at', new Date().toISOString())
        .gt('last_activity', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()) // Within 30 days
        .order('last_activity', { ascending: false });

      if (error) {
        console.error('Error fetching active sessions:', error);
        return [];
      }

      return data.map(session => ({
        id: session.id,
        sessionId: session.session_id,
        deviceInfo: session.device_info as DeviceInfo,
        lastActivity: session.last_activity,
        createdAt: session.created_at,
        expiresAt: session.expires_at
      }));
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      return [];
    }
  }

  // Check if current session is valid
  async isSessionValid(): Promise<boolean> {
    if (!this.currentSessionId) return false;

    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('session_id', this.currentSessionId)
        .gt('expires_at', new Date().toISOString())
        .single();

      return !error && !!data;
    } catch (error) {
      return false;
    }
  }

  // Delete a specific session (for logout from specific device)
  async deleteSession(sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .delete()
        .eq('session_id', sessionId);

      if (error) {
        console.error('Error deleting session:', error);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  }

  // Delete all sessions for a user (logout from all devices)
  async deleteAllSessions(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error deleting all sessions:', error);
      }

      // Broadcast logout event to all sessions
      await this.broadcastLogout(userId);
    } catch (error) {
      console.error('Error deleting all sessions:', error);
    }
  }

  // Broadcast logout event to all sessions
  private async broadcastLogout(userId: string, sessionId?: string): Promise<void> {
    try {
      // Use Supabase's broadcast feature to notify all connected clients
      await supabase.channel('logout_notifications').send({
        type: 'broadcast',
        event: 'user_logout',
        payload: {
          userId,
          sessionId,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Error broadcasting logout:', error);
    }
  }

  // Clean up expired sessions (should be called periodically)
  async cleanupExpiredSessions(): Promise<number> {
    try {
      // Manually delete expired sessions since RPC function may not be in types yet
      const { error, count } = await supabase
        .from('user_sessions')
        .delete({ count: 'exact' })
        .or(`expires_at.lt.${new Date().toISOString()},last_activity.lt.${new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()}`);

      if (error) {
        console.error('Error cleaning up expired sessions:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error cleaning up expired sessions:', error);
      return 0;
    }
  }

  // Start heartbeat to keep session alive
  private startHeartbeat(): void {
    // Prevent duplicate intervals
    if (this.heartbeatInterval) {
      return;
    }
    
    // Update session activity every 5 minutes
    this.heartbeatInterval = setInterval(() => {
      this.updateSessionActivity();
    }, 5 * 60 * 1000);
  }

  // Stop heartbeat
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Start periodic cleanup of stale sessions and offline users
  private startPeriodicCleanup(): void {
    // Prevent duplicate intervals
    if (this.cleanupInterval) {
      return;
    }
    
    // Run cleanup every 30 minutes
    this.cleanupInterval = setInterval(async () => {
      try {
        // Clean up expired sessions
        const cleanedCount = await this.cleanupExpiredSessions();
        if (cleanedCount > 0) {
          console.log(`Cleaned up ${cleanedCount} expired sessions`);
        }

        // Mark users offline if they have no active sessions
        await this.markInactiveUsersOffline();
      } catch (error) {
        console.error('Error during periodic cleanup:', error);
      }
    }, 30 * 60 * 1000); // 30 minutes
  }

  // Stop periodic cleanup
  private stopPeriodicCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  // Mark users offline if they have no active sessions
  private async markInactiveUsersOffline(): Promise<void> {
    try {
      // Find users who are currently online but have no active sessions
      const { data: onlineUsers, error } = await supabase
        .from('profiles')
        .select('id, status, updated_at')
        .in('status', ['online', 'away', 'busy'])
        .gt('updated_at', new Date(Date.now() - 10 * 60 * 1000).toISOString()); // Updated in last 10 minutes

      if (error || !onlineUsers) {
        console.error('Error fetching online users:', error);
        return;
      }

      for (const user of onlineUsers) {
        // Check if this user has any active sessions
        const activeSessions = await this.getActiveSessions(user.id);

        if (activeSessions.length === 0) {
          // No active sessions - mark as offline
          console.log(`Marking user ${user.id} offline - no active sessions`);
          try {
            await supabase
              .from('profiles')
              .update({
                status: 'offline',
                updated_at: new Date().toISOString()
              })
              .eq('id', user.id);
          } catch (error) {
            console.error(`Error marking user ${user.id} offline:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Error marking inactive users offline:', error);
    }
  }

  // Start user-specific services (called when user is initialized)
  startUserServices(): void {
    if (!this.logoutChannel) {
      // Set up logout event listener
      this.setupLogoutListener();
    }

    if (!this.heartbeatInterval) {
      // Start heartbeat to keep session alive
      this.startHeartbeat();
    }

    if (!this.cleanupInterval) {
      // Start periodic cleanup
      this.startPeriodicCleanup();
    }
  }

  // Clean up when user logs out
  async cleanup(): Promise<void> {
    this.stopHeartbeat();
    this.stopPeriodicCleanup();

    // Unsubscribe from logout channel
    if (this.logoutChannel) {
      supabase.removeChannel(this.logoutChannel);
      this.logoutChannel = null;
    }

    // Delete current session if it exists
    if (this.currentSessionId) {
      await this.deleteSession(this.currentSessionId);
    }

    this.currentSessionId = null;
    this.logoutListeners.clear();
  }

  // Get preferred status from database
  async getPreferredStatus(userId: string): Promise<UserStatus | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('preferred_status')
        .eq('id', userId)
        .single();

      if (error || !data) return null;

      return data.preferred_status as UserStatus;
    } catch (error) {
      console.error('Error fetching preferred status:', error);
      return null;
    }
  }

  // Save preferred status to database
  async savePreferredStatus(userId: string, status: UserStatus): Promise<void> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ preferred_status: status })
        .eq('id', userId);

      if (error) {
        console.error('Error saving preferred status:', error);
      }
    } catch (error) {
      console.error('Error saving preferred status:', error);
    }
  }
}

// Export singleton instance
export const sessionService = new SessionService();
export default sessionService;